// @flow
import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";

import { HTTP_METHODS, MODULARUI_STATUS } from "../constants";
import {
  loadModularUI,
  removeModelByKey,
} from "../redux/_modularui/ModularUIActions";

import useDeepCompareEffect from "./useDeepCompareEffect";
import { useModularUIKey } from "./useModularUIKey";

import Href from "../models/href/Href";

import type { RequestModularUIOptions } from "../utils";
import type { ModelEntry } from "../redux";

/**
 * A custom hook to fetch and manage Be Informed modular UI resources.
 * It handles automatic data fetching based on URL/query changes, manages Redux state
 * synchronization, and provides cleanup logic when components unmount.
 *
 * @param {string} modelKey - A unique identifier for the type of model being fetched.
 * @param {string | Href} url - The endpoint URL or Href instance to fetch data from.
 * @param {RequestModularUIOptions} options - Configuration for the request (method, headers, etc.).
 * @returns {ModelEntry} The model data retrieved from the Redux store.
 */
export const useModularUI = (
  modelKey: string,
  url: string | Href,
  options: RequestModularUIOptions = (({}: any): RequestModularUIOptions),
): any => {
  const dispatch = useDispatch();

  // Normalize the URL input into a Href instance to safely access query params and paths
  const hrefInstance = useMemo(() => new Href(url), [url]);
  const requestUrl = useMemo(() => hrefInstance.toString(), [hrefInstance]);
  const currentQueryString = hrefInstance.querystring;

  // Persistence ref to detect if query parameters have changed between renders
  const prevQueryRef = useRef(currentQueryString);

  // Generate a unique key for the Redux store based on the model type and the full URL
  const key = useModularUIKey(modelKey, requestUrl);

  // Select the specific piece of state corresponding to this modular UI component
  const model = useSelector((state) => state.modularui[key]);

  /**
   * Memoize request options to prevent unnecessary re-renders.
   * Merges default settings (GET method, auto-cleanup) with user-provided options.
   */
  const requestOptions = useMemo(() => {
    const merged = {
      method: HTTP_METHODS.GET,
      removeOnUnmount: true,
      ...options,
    };

    if (url instanceof Href) {
      merged.origin = merged.origin ?? url.origin;
      merged.contextPath = merged.contextPath ?? url.contextPath;
    }
    return merged;
  }, [options, url]);

  const location = useLocation();
  const redirectLocation = location.state?.redirectLocation;

  /**
   * Determines if a network request should be initiated.
   * Logic includes:
   * - Model doesn't exist yet (Initial load)
   * - Previous request resulted in an error (Retry)
   * - The query parameters changed (Filtering/Paging)
   * - A redirect was triggered specifically for this URL
   * - Manual 'isReload' flag is passed in options
   */
  const shouldLoad =
    model == null ||
    model.status === MODULARUI_STATUS.ERROR ||
    prevQueryRef.current !== currentQueryString ||
    (redirectLocation instanceof Href
      ? redirectLocation.equals(requestUrl)
      : false) ||
    requestOptions.isReload === true;

  // Synchronize the ref after the render cycle determines if we should load
  useEffect(() => {
    prevQueryRef.current = currentQueryString;
  }, [currentQueryString]);

  // Execute the load action. Deep compare is used on options to prevent
  // infinite loops caused by passing inline object literals.
  useDeepCompareEffect(() => {
    if (requestUrl !== "" && shouldLoad) {
      dispatch(loadModularUI(key, requestUrl, requestOptions));
    }
  }, [key, requestUrl, requestOptions, shouldLoad]);

  // Cleanup Effect:
  // If 'removeOnUnmount' is true, this removes the model data from the Redux store
  // when the component using this hook is destroyed.
  useEffect(() => {
    if (requestOptions.removeOnUnmount) {
      return () => {
        dispatch(removeModelByKey(key));
      };
    }
  }, [dispatch, key, requestOptions.removeOnUnmount]);

  return model;
};
